/**
 * One published report, opened from Insights (Ben, 2026-10-23). "View"
 * used to navigate straight to the API address, so a slow store showed a
 * blank tab for up to a minute and a half. It opens this page instead:
 * the report's title, a spinner and a word about large reports, with the
 * page itself loading in a frame below.
 *
 * The frame is sandboxed exactly as the page's own policy is
 * (insight_packs_api.PACK_PAGE_CSP: `sandbox allow-scripts`), so framing it
 * gives the report nothing it did not have in its own tab.
 *
 * A sandboxed frame's status code cannot be read from outside it, so the
 * frame asks for ?embed=1: a refusal (503 store unavailable, 404) then
 * answers as a tiny page that posts {priori: 'report-error', status} here.
 * Without word from the frame, `load` means the report is showing. After
 * SLOW_MS the spinner says so; after GIVE_UP_MS with nothing, it offers
 * Try again — as it does on a 503.
 *
 * A refusal that is NOT the embed page — a 401 from a session that expired,
 * answered by the auth layer before the view runs — would `load` as if it
 * were the report and show its JSON (SDET review of #888). So each attempt
 * first asks for this club's report list (which also gives the title): a
 * 401 there says signed out, a run not in it says not available, and only
 * then is the frame loaded.
 *
 * The theme travels as InsightPackViewer sends it (DS-023): a snapshot in
 * the URL, read before first paint, then priori.theme messages on load and
 * on every change. Without them the page drew light inside a dark app.
 */
import { Loader2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { ApiError } from '../lib/api'
import { alignToReport } from '../lib/orgAlign'
import { clubReportDownloadUrl, clubReportEmbedUrl, listClubReports } from '../packs/promotions'
import {
  matchVideoWindowName, useMatchVideoWindow, usePackVideoSource,
} from '../packs/videoHandoff'
import { useCurrentTheme } from '../theme/useCurrentTheme'

import './insights.css'

/** "Still loading…" after this long. */
export const SLOW_MS = 30_000
/** Give up and offer Try again after this long with no answer: past the
 *  server's own bound (two ~12 s reads), so a 503 normally arrives first. */
export const GIVE_UP_MS = 60_000

type Phase = 'loading' | 'slow' | 'shown' | 'unavailable' | 'missing' | 'timeout'
  | 'signedout'

export default function ReportViewer() {
  const { runId: raw } = useParams()
  const runId = Number(raw)
  const [title, setTitle] = useState<string | null>(null)
  const [gameCode, setGameCode] = useState<string | null>(null)
  const [phase, setPhase] = useState<Phase>('loading')
  const [attempt, setAttempt] = useState(0)
  // the frame is loaded only once this attempt's check has passed
  const [framed, setFramed] = useState(false)
  const frame = useRef<HTMLIFrameElement>(null)
  // set before `load` when the frame reported a refusal, so load does not
  // read the refusal page as the report
  const refused = useRef(false)
  // a snapshot in src (a src change would reload the page); changes go by
  // message, '*' because the sandboxed frame's origin is null
  const theme = useCurrentTheme()
  const initialTheme = useRef(theme).current
  const postTheme = useCallback(() => {
    frame.current?.contentWindow?.postMessage({ type: 'priori.theme', theme }, '*')
  }, [theme])
  useEffect(() => { postTheme() }, [postTheme])

  // per attempt: is the session good and the report this club's?
  useEffect(() => {
    let on = true
    setFramed(false)
    listClubReports()
      .then((d) => {
        if (!on) return
        const r = d.reports.find((x) => x.run_id === runId)
        if (!r) {
          // A link shared from another club: staff are switched into the
          // report's club and the page reloads there; anyone else (and an
          // unknown report) sees "missing" as before.
          if (!Number.isFinite(runId)) { setPhase('missing'); return }
          void alignToReport(runId).then((switched) => {
            if (!on) return
            if (switched) window.location.reload()
            else setPhase('missing')
          })
          return
        }
        setTitle(`${r.title} — ${r.game_code}`)
        setGameCode(r.game_code)
        setFramed(true)
      })
      .catch((e: unknown) => {
        if (!on) return
        if (e instanceof ApiError && e.isUnauthenticated) { setPhase('signedout'); return }
        // the list could not say (a blip): try the page itself, which
        // reports its own refusals
        setFramed(true)
      })
    return () => { on = false }
  }, [runId, attempt])

  // per attempt: the timers, and the frame's word
  useEffect(() => {
    refused.current = false
    setPhase('loading')
    const slow = window.setTimeout(
      () => setPhase((p) => (p === 'loading' ? 'slow' : p)), SLOW_MS)
    const giveUp = window.setTimeout(
      () => setPhase((p) => (p === 'loading' || p === 'slow' ? 'timeout' : p)),
      GIVE_UP_MS)
    const onMessage = (e: MessageEvent) => {
      // the frame is an opaque origin ('null'): trust the SOURCE, not origin
      if (e.source !== frame.current?.contentWindow) return
      const d = e.data as { priori?: string; status?: number } | null
      if (d?.priori !== 'report-error') return
      refused.current = true
      setPhase(d.status === 404 ? 'missing' : 'unavailable')
    }
    window.addEventListener('message', onMessage)
    return () => {
      window.clearTimeout(slow)
      window.clearTimeout(giveUp)
      window.removeEventListener('message', onMessage)
    }
  }, [attempt, runId])

  // Video (packs/videoHandoff), as the pack viewer: a page that plays it
  // itself is answered with its source; one that does not gets the match
  // video in its own window on a moment, with a link when that is blocked.
  // It never answered either, so a report's moments could not play
  // (Ben, 2026-10-30). The window waits MOMENT_GRACE_MS for a source
  // request: older pack pages send it after their first moment.
  const playsInline = usePackVideoSource(frame, gameCode)
  const { blockedVideo, cancel: cancelVideo } =
    useMatchVideoWindow(frame, gameCode, playsInline)

  const busy = phase === 'loading' || phase === 'slow'
  const retry = phase === 'unavailable' || phase === 'timeout'

  return (
    <div className="ins-viewer">
      <div className="ins-viewer__bar">
        <Link to="/insights" className="ins-viewer__back">← Insights</Link>
        <h1 className="ins-viewer__title">{title ?? 'Report'}</h1>
        {Number.isFinite(runId) && (
          <a className="ins-viewer__dl" href={clubReportDownloadUrl(runId)}>Download</a>
        )}
      </div>
      {busy && (
        <div className="ins-viewer__status" role="status">
          <Loader2 size={18} className="ins-viewer__spin" aria-hidden="true" />
          <span>
            {phase === 'slow' ? 'Still loading…' : 'Opening the report…'}
            {' '}Large reports can take up to a minute to open.
          </span>
        </div>
      )}
      {phase === 'signedout' && (
        <div className="ins-viewer__status" role="alert">
          Your session has ended. Sign in again to open this report.
        </div>
      )}
      {phase === 'missing' && (
        <div className="ins-viewer__status" role="alert">
          This report is not available. <Link to="/insights">Back to Insights</Link>
        </div>
      )}
      {retry && (
        <div className="ins-viewer__status" role="alert">
          <span>
            {phase === 'timeout'
              ? 'The report is taking too long to open.'
              : 'The report cannot be opened right now.'}
          </span>
          <button type="button" className="ins-viewer__retry"
                  onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </div>
      )}
      {blockedVideo && gameCode && (
        <div className="ins-viewer__status" role="status">
          The browser blocked the video window.{' '}
          <a href={blockedVideo} target={matchVideoWindowName(gameCode)}>Open the video</a>
        </div>
      )}
      {Number.isFinite(runId) && framed && (
        <iframe key={attempt} ref={frame}
                className={`ins-viewer__frame${phase === 'shown' ? '' : ' is-pending'}`}
                title={title ?? 'Report'}
                src={clubReportEmbedUrl(runId, initialTheme)}
                sandbox="allow-scripts"
                onLoad={() => {
                  cancelVideo()
                  postTheme()
                  if (!refused.current) setPhase('shown')
                }} />
      )}
    </div>
  )
}
